import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from "@angular/core";
import { CommonModule, DatePipe, Location } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { doc, getDoc, updateDoc, Timestamp, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "./firebase";
import { News } from "./models/news.model";
import { AuthService } from "./auth.service";
import { CountFormatPipe } from "./count-format.pipe";

@Component({
  selector: "app-news-detail",
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule, CountFormatPipe],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 flex flex-col font-sans relative">

      <!-- Header Block: Sticky Modern Header -->
      <header class="sticky top-0 z-50 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border-b border-black/5 dark:border-white/5 shadow-sm px-4 h-16 md:h-20 flex items-center justify-between">
        <div class="max-w-4xl mx-auto w-full flex items-center gap-4 relative">
          
          <!-- Back button -->
          <button
            (click)="goBack()"
            class="h-10 px-3 md:px-4 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 text-ios-blue hover:text-ios-blue flex items-center gap-1 font-bold text-xs md:text-sm transition-all active:scale-95 cursor-pointer flex-shrink-0"
          >
            <mat-icon>arrow_back_ios</mat-icon>
            <span class="tracking-tight hidden md:inline">Back to Org</span>
          </button>

          <!-- Center Title -->
          <div class="flex-1 text-center px-4 overflow-hidden">
            <h1 class="text-sm md:text-lg font-black tracking-tight truncate uppercase opacity-80 decoration-ios-blue hover:underline">
              {{ newsItem()?.title || 'Loading Bulletin...' }}
            </h1>
          </div>
          
          <!-- Fake placeholder to balance the layout centering -->
          <div class="w-10 md:w-[100px] flex-shrink-0"></div>
        </div>
      </header>

      <!-- Loading State -->
      @if (isLoading()) {
        <div class="flex-1 flex flex-col items-center justify-center py-24">
          <div class="w-12 h-12 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin mb-4"></div>
          <p class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40">Retrieving Bulletin Content</p>
        </div>
      }

      <!-- Error State -->
      @if (errorMessage()) {
        <div class="flex-1 max-w-lg mx-auto w-full px-4 flex flex-col items-center justify-center text-center py-24 animate-ios-slide">
          <div class="w-16 h-16 rounded-2xl bg-ios-red/10 text-ios-red flex items-center justify-center mb-6">
            <mat-icon class="!text-3xl">error_outline</mat-icon>
          </div>
          <h3 class="text-2xl font-bold tracking-tight mb-2">Bulletin Lost</h3>
          <p class="text-sm text-black/50 dark:text-white/40 leading-relaxed mb-8">
            {{ errorMessage() }}
          </p>
          <button
            (click)="goBack()"
            class="h-12 px-6 rounded-2xl bg-black/5 dark:bg-white/5 hover:bg-black/10 text-sm font-bold flex items-center gap-2 transition-all active:scale-95"
          >
            <mat-icon>arrow_back</mat-icon>
            <span>Return to Organization</span>
          </button>
        </div>
      }

      <!-- Body Content -->
      @if (!isLoading() && !errorMessage() && newsItem()) {
        <main class="flex-1 py-8 px-4 md:py-12 animate-ios-slide custom-scrollbar">
          <div class="max-w-3xl mx-auto space-y-8">
            
            <!-- Category Pill & Title Block -->
            <div class="space-y-4">
              <div class="flex items-center gap-2.5">
                <span class="px-3.5 py-1.5 rounded-full bg-ios-blue/10 dark:bg-ios-blue/15 text-ios-blue border border-ios-blue/10 uppercase font-black text-[10px] tracking-widest">
                  {{ newsItem()?.category }}
                </span>
                <span class="w-1.5 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full"></span>
                <span class="text-[10px] font-black uppercase tracking-wider opacity-40 flex items-center gap-1">
                  <mat-icon class="!text-xs">newspaper</mat-icon> Verified Post
                </span>
              </div>

              <h2 class="text-2xl md:text-4xl lg:text-5xl font-black tracking-tight text-black dark:text-white leading-[1.12]">
                {{ newsItem()?.title }}
              </h2>
            </div>

            <!-- Dynamic Gallery Framework -->
            @if (newsItem()?.bannerImage) {
              <div class="space-y-4">
                
                <!-- Main Prominent Preview Area -->
                <div class="aspect-video w-full rounded-[36px] bg-black/5 dark:bg-zinc-900/50 border border-black/5 dark:border-white/5 overflow-hidden relative shadow-ios shadow-black/5">
                  <img
                    [src]="newsItem()!.bannerImage"
                    [alt]="newsItem()!.title + ' media frame'"
                    class="w-full h-full object-cover transition-opacity duration-300"
                    referrerpolicy="no-referrer"
                  />
                </div>
              </div>
            }

            <!-- Content Block: Description & Body -->
            <article class="p-8 md:p-10 rounded-[32px] bg-white dark:bg-zinc-900/50 border border-black/5 dark:border-white/5 shadow-sm">
              <p class="whitespace-pre-line text-base md:text-lg leading-relaxed text-zinc-700 dark:text-zinc-300 font-medium tracking-normal select-text">
                {{ newsItem()?.content }}
              </p>
            </article>

            <!-- Analytics & Engagement Row & Likes Toggle interaction -->
            <div class="p-6 rounded-2xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5 flex flex-wrap items-center justify-between gap-6">
              
              <!-- Metrics Column -->
              <div class="flex items-center gap-6">
                <div class="flex items-center gap-2 text-zinc-500 dark:text-zinc-400">
                  <mat-icon class="!text-xl text-zinc-400">visibility</mat-icon>
                  <div class="text-sm">
                    <span class="font-extrabold text-zinc-900 dark:text-white">{{ (newsItem()?.viewedBy?.length || 0) | countFormat }}</span>
                    <span class="text-xs opacity-60 ml-1 uppercase tracking-wider font-black">Views</span>
                  </div>
                </div>

                <div class="flex items-center gap-2 text-zinc-500 dark:text-zinc-400">
                  <mat-icon class="!text-xl" [class.text-ios-red]="hasLiked()" [class.text-zinc-400]="!hasLiked()">favorite</mat-icon>
                  <div class="text-sm">
                    <span class="font-extrabold text-zinc-900 dark:text-white">{{ (newsItem()?.likedBy?.length || 0) | countFormat }}</span>
                    <span class="text-xs opacity-60 ml-1 uppercase tracking-wider font-black">Likes</span>
                  </div>
                </div>
              </div>

              <!-- Like Action Toggle -->
              <button
                type="button"
                (click)="toggleLike()"
                [disabled]="isLiking()"
                class="h-12 px-6 rounded-2xl border flex items-center gap-2 text-sm font-bold active:scale-95 transition-all shadow-sm cursor-pointer hover:border-ios-red/40"
                [class.bg-ios-red/10]="hasLiked()"
                [class.text-ios-red]="hasLiked()"
                [class.border-ios-red/20]="hasLiked()"
                [class.bg-white]="!hasLiked()"
                [class.dark:bg-zinc-900]="!hasLiked()"
                [class.text-zinc-700]="!hasLiked()"
                [class.dark:text-zinc-300]="!hasLiked()"
                [class.border-black/5]="!hasLiked()"
                [class.dark:border-white/5]="!hasLiked()"
              >
                <mat-icon [class.fill-current]="hasLiked()">
                  {{ hasLiked() ? 'favorite' : 'favorite_border' }}
                </mat-icon>
                <span>{{ hasLiked() ? 'Loved!' : 'Like Bulletin' }}</span>
              </button>
            </div>

            <!-- Audit Timestamps Block -->
            <footer class="p-8 rounded-[30px] border border-black/5 dark:border-white/5 bg-black/[0.01] dark:bg-white/[0.01] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs">
              
              <div class="space-y-1">
                <p class="font-black uppercase tracking-widest text-[9px] text-zinc-400">Published On</p>
                <p class="font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                  <mat-icon class="!text-sm text-zinc-400">calendar_today</mat-icon>
                  {{ formatDate(newsItem()?.createdAt) }}
                </p>
              </div>

              @if (newsItem()?.updatedAt) {
                <div class="space-y-1 border-t sm:border-t-0 sm:border-l border-black/5 dark:border-white/5 pt-4 sm:pt-0 sm:pl-6">
                  <p class="font-black uppercase tracking-widest text-[9px] text-ios-purple">Last Updated/Edited</p>
                  <p class="font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                    <mat-icon class="!text-sm text-ios-purple opacity-70">update</mat-icon>
                    {{ formatDate(newsItem()?.updatedAt) }}
                  </p>
                </div>
              }
            </footer>

          </div>
        </main>
      }

    </div>
  `,
  styles: [`
    .custom-scrollbar::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 99px;
    }
    .dark .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.155);
    }
  `],
})
export class NewsDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private datePipe = inject(DatePipe);
  authService = inject(AuthService);

  newsId = signal<string | null>(null);
  newsItem = signal<News | null>(null);
  isLoading = signal<boolean>(true);
  errorMessage = signal<string | null>(null);

  // Gallery Navigation State
  activeImageIndex = signal<number>(0);

  // Likes Local state to prevent double liking
  hasLiked = signal<boolean>(false);
  isLiking = signal<boolean>(false);

  ngOnInit() {
    this.route.paramMap.subscribe(async (params) => {
      const id = params.get("newsId") || params.get("id");
      const ownerId = params.get("ownerId") || this.route.snapshot.queryParamMap.get("owner");
      this.newsId.set(id);
      if (id) {
        await this.loadNewsItem(id, ownerId);
      } else {
        this.isLoading.set(false);
        this.errorMessage.set("No news identifier provided in route.");
      }
    });
  }

  async loadNewsItem(id: string, routeOwnerId?: string | null) {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      let data: any = null;
      let docId = id;
      const ownerId = routeOwnerId || this.route.snapshot.queryParamMap.get("owner");
      if (ownerId) {
        const newsDocRef = doc(db, "accounts", ownerId, "news", id);
        const newsSnap = await getDoc(newsDocRef);
        if (newsSnap.exists()) {
          data = newsSnap.data();
          docId = newsSnap.id;
        }
      }

      if (!data) {
        const { collectionGroup, query, where, getDocs } = await import("firebase/firestore");
        const q = query(collectionGroup(db, "news"), where("id", "==", id));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const firstDoc = snap.docs[0];
          data = firstDoc.data();
          docId = firstDoc.id;
        }
      }

      if (data) {
        // Parse dates safely from Timestamp / Date
        const createdAt = this.parseDate(data["createdAt"]);
        const updatedAt = data["updatedAt"] ? this.parseDate(data["updatedAt"]) : undefined;

        this.newsItem.set({
          id: docId,
          title: data["title"] || "",
          content: data["content"] || data["description"] || "",
          bannerImage: data["bannerImage"] || (data["images"] && data["images"].length > 0 ? data["images"][0] : ""),
          category: data["category"] || "General",
          source: data["source"] || "",
          status: data["status"] || "published",
          verificationStatus: data["verificationStatus"] || "review",
          verificationRemarks: data["verificationRemarks"] || "",
          verifiedBy: data["verifiedBy"] || "",
          authorUid: data["authorUid"] || "",
          ownerUid: data["ownerUid"] || "",
          ownedBy: data["ownedBy"] || data["ownerUid"] || "",
          uploadedBy: data["uploadedBy"] || data["authorUid"] || "",
          createdAt: createdAt,
          updatedAt: updatedAt || createdAt,
          views: data["views"] || [],
          likes: data["likes"] || [],
          viewedBy: data["viewedBy"] || data["views"] || [],
          likedBy: data["likedBy"] || data["likes"] || [],
        });
        
        const parsedLikes = data["likedBy"] || data["likes"] || [];
        this.hasLiked.set(parsedLikes.includes(this.authService.user()?.uid || ''));
        await this.incrementViews(docId, data["ownerUid"]);
      } else {
        this.errorMessage.set("News item does not exist, or has been deleted from the network.");
      }
    } catch (error) {
      console.error("Error retrieving news detail", error);
      this.errorMessage.set("Error fetching news content. Please check networking/permissions.");
    } finally {
      this.isLoading.set(false);
    }
  }

  async incrementViews(id: string, ownerUid: string) {
    if (!ownerUid) return;
    try {
      const currentUserUid = this.authService.user()?.uid || "anonymous_session_" + Math.random().toString(36).substring(2, 9);
      const newsDocRef = doc(db, "accounts", ownerUid, "news", id);
      await updateDoc(newsDocRef, {
        viewedBy: arrayUnion(currentUserUid)
      });
      // Update local state views array silently
      const item = this.newsItem();
      if (item && !item.viewedBy?.includes(currentUserUid)) {
        this.newsItem.set({
          ...item,
          viewedBy: [...(item.viewedBy || []), currentUserUid]
        });
      }
    } catch (e) {
      console.warn("Silent failure to increment view array", e);
    }
  }

  async toggleLike() {
    const id = this.newsId();
    const item = this.newsItem();
    const currentUser = this.authService.user();
    if (!id || !item || !item.ownerUid || this.isLiking() || !currentUser) return;

    const currentLikes = item.likedBy || item.likes || [];
    const isCurrentlyLiked = currentLikes.includes(currentUser.uid);

    if (isCurrentlyLiked) {
      // Unlike
      this.isLiking.set(true);
      try {
        const newsDocRef = doc(db, "accounts", item.ownerUid, "news", id);
        await updateDoc(newsDocRef, {
          likedBy: arrayRemove(currentUser.uid)
        });
        this.newsItem.set({
          ...item,
          likedBy: currentLikes.filter(u => u !== currentUser.uid)
        });
        this.hasLiked.set(false);
      } catch (err) {
        console.error("Error toggling unlike", err);
      } finally {
        this.isLiking.set(false);
      }
    } else {
      // Like
      this.isLiking.set(true);
      try {
        const newsDocRef = doc(db, "accounts", item.ownerUid, "news", id);
        await updateDoc(newsDocRef, {
          likedBy: arrayUnion(currentUser.uid)
        });
        this.newsItem.set({
          ...item,
          likedBy: [...currentLikes, currentUser.uid]
        });
        this.hasLiked.set(true);
      } catch (err) {
        console.error("Error toggling like", err);
      } finally {
        this.isLiking.set(false);
      }
    }
  }

  private parseDate(val: unknown): Date {
    if (!val) return new Date();
    if (val instanceof Date) return val;
    if (typeof val === "object" && val !== null && "toDate" in val && typeof (val as { toDate: () => Date }).toDate === "function") {
      return (val as { toDate: () => Date }).toDate();
    }
    if (val instanceof Timestamp) {
      return val.toDate();
    }
    try {
      return new Date(val as string | number);
    } catch {
      return new Date();
    }
  }

  private location = inject(Location);

  goBack() {
    const hasPreviousPage =
      typeof window !== "undefined" &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(["/search"]);
    }
  }

  formatDate(date: Date | Timestamp | undefined): string {
    if (!date) return "";
    const parsedDate = this.parseDate(date);
    return this.datePipe.transform(parsedDate, "MMMM d, yyyy h:mm a") || parsedDate.toLocaleDateString();
  }
}
